import { useBreakpoint } from '@react/services/breakpoint';
import { Chevron } from '@sly/icons/react';
import cx from 'classnames';
import { bool, node, object, string } from 'proptypes';
import React, { forwardRef, useCallback, useMemo, useState } from 'react';
import { Heading } from 'sly/system';

const Collapsible = forwardRef(
  (
    {
      children,
      title,
      borderBottom = '1',
      borderColor = 'slate.lighter-90',
      disabled = false,
      showIf = true,
      ...props
    },
    ref
  ) => {
    const [userCollapsed, setCollapsed] = useState(null);
    const breakpoint = useBreakpoint();
    const collapsed = useMemo(() => {
      if (userCollapsed === null) {
        return !breakpoint?.atLeastTablet();
      }
      return userCollapsed;
    }, [userCollapsed, breakpoint]);
    const [height, setHeight] = useState(null);
    const contentRef = useCallback((element) => {
      if (element?.scrollHeight) {
        setHeight(element.scrollHeight);
      }
    }, []);
    const toggle = useCallback(() => {
      setCollapsed(!collapsed);
    }, [collapsed]);

    if (!showIf) {
      return null;
    }

    return (
      <div
        ref={ref}
        className={cx(`border-[${borderColor}]`)}
        style={{
          borderBottomWidth: !disabled ? `${borderBottom || '1'}px` : '',
        }}
        data-testid='collapsible'
        {...props}
      >
        <div
          onClick={(!disabled && toggle) || null}
          className={cx(
            'collapsibleHeader',
            'flex',
            'pt-l',
            collapsed ? 'pb-l' : 'pb-s'
          )}
        >
          <Heading className='font-t-m max-w-full overflow-hidden text-ellipsis p-0'>
            {title}
          </Heading>
          {!disabled && (
            <Chevron
              className={cx('ml-auto', collapsed ? 'rotate-180' : '')}
              color='text-slate-base'
            />
          )}
        </div>
        <div
          ref={contentRef}
          className={cx('collapsibleContent')}
          style={{
            height: collapsed ? '0px' : `${height}px`,
          }}
        >
          {children}
        </div>
      </div>
    );
  }
);

Collapsible.displayName = 'Collapsible';

Collapsible.propTypes = {
  children: node,
  title: string.isRequired,
  innerRef: object,
  showIf: bool,
  disabled: bool,
  borderBottom: string,
  borderColor: string,
};

export default Collapsible;
